import React, { useRef, useState, useEffect, useMemo } from "react";
import SplitPanel, { SplitPanelTheme } from "./split-panels/SplitPanel";
import TopBar from "./top-panel/TopBar";
import BottomBar from "./bottom-panel/BottomBar";
import SideBar from "./left-side-panel/SideBar";
import ArcGISMap from "./map-panel/ArcGISMap";
import appConfig, { panelConfigs } from "./split-panels/PanelConfig";
import { ArcGISMapHandle } from "../types/config";
import config from "../config/config.json";
import sharedConfig from "../config/search.json";
import type { AppJsonConfig } from "../types/config";
import { queryLayersAtPoint } from "../utils/queryContextLayers";
import { getDomainGuide } from "../utils/domains";
import { applyDomains } from "../utils/domains";
import { CalciteLoader } from "@esri/calcite-components-react";
import DisclaimerModal from "./modals/DisclaimerModal";
import GeoJobeLoader from "../components/geo-jobe-loader/GeoJobeLoader";
import "../style.css";

type PanelLike = {
  label: string;
  content: string;
  source?: "parcelsLayer" | "layersLayer";
  layerNumber?: number;
  layerUrl?: string;
};

type LayerAttrs = Record<string, Record<number, any[]>>; // root -> layerId -> rows

const Workspace: React.FC = () => {
  const [resultRows, setResultRows] = useState<any[]>([]);
  const [selectedAttrs, setSelectedAttrs] = useState<Record<string, any>>({});
  const [layerAttrs, setLayerAttrs] = useState<LayerAttrs>({});
  const countyCfg = config as AppJsonConfig;
  const layout = countyCfg.enabledModules?.sidebar?.propertyDetailsLayout ?? [];
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  // Detect mobile view for responsive sidebar sizing
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Map of rootUrl -> layerIds used by "layersLayer" panels
  const contextLayersByRoot = useMemo(() => {
    const map: Record<string, Set<number>> = {};
    const defaultRoot = (countyCfg as any).layersLayer || "";

    for (const tab of layout as { panels?: PanelLike[] }[]) {
      for (const p of (tab.panels || []) as PanelLike[]) {
        if (p.source === "layersLayer" && typeof p.layerNumber === "number") {
          const rootRaw = (p.layerUrl || defaultRoot || "").trim();
          if (!rootRaw) continue;
          const root = rootRaw.replace(/\/+$/, "");
          if (!map[root]) map[root] = new Set<number>();
          map[root].add(p.layerNumber);
        }
      }
    }

    const out: Record<string, number[]> = {};
    for (const root of Object.keys(map)) {
      out[root] = Array.from(map[root]);
    }
    return out;
  }, [layout, countyCfg]);

  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const [openResultsPanel, setOpenResultsPanel] = useState(false);

  const mapRef = useRef<ArcGISMapHandle>(null);

  const [busyCount, setBusyCount] = useState(0);
  const startBusy = () => setBusyCount((c) => c + 1);
  const endBusy = () => setBusyCount((c) => Math.max(0, c - 1));
  const [appLoading, setAppLoading] = useState(true);

  const [panelCollapseDirections, setPanelCollapseDirections] = useState<
    Record<string, "up" | "down" | "left" | "right">
  >(() => {
    const initial: Record<string, "up" | "down" | "left" | "right"> = {};
    panelConfigs.forEach((cfg) => (initial[cfg.id] = cfg.defaultCollapseDirection));
    return initial;
  });

  const splitPanelTheme: SplitPanelTheme = appConfig.splitPanelTheme;

  const topPanelConfig = panelConfigs.find((c) => c.id === "topPanel")!;
  const bottomPanelConfig = panelConfigs.find((c) => c.id === "bottomPanel")!;
  const leftPanelConfigBase = panelConfigs.find((c) => c.id === "leftPanel")!;

  // Increase left panel size on mobile for better usability
  const leftPanelConfig = useMemo(() => ({
    ...leftPanelConfigBase,
    initialSize: isMobile ? 65 : leftPanelConfigBase.initialSize,
    maxResize: isMobile ? 65 : leftPanelConfigBase.maxResize,
  }), [isMobile, leftPanelConfigBase]);

  const topPanelProps =
    topPanelConfig.defaultSplit === "h"
      ? { split: "h" as const, collapseDirection: panelCollapseDirections["topPanel"] as "up" | "down" }
      : { split: "v" as const, collapseDirection: panelCollapseDirections["topPanel"] as "left" | "right" };

  const bottomPanelProps =
    bottomPanelConfig.defaultSplit === "h"
      ? { split: "h" as const, collapseDirection: panelCollapseDirections["bottomPanel"] as "up" | "down" }
      : { split: "v" as const, collapseDirection: panelCollapseDirections["bottomPanel"] as "left" | "right" };

  const leftPanelProps =
    leftPanelConfig.defaultSplit === "h"
      ? { split: "h" as const, collapseDirection: panelCollapseDirections["leftPanel"] as "up" | "down" }
      : { split: "v" as const, collapseDirection: panelCollapseDirections["leftPanel"] as "left" | "right" };

  const [activeRoot, setActiveRoot] = useState<string | null>(null);

  async function loadContextForSelection(sel: any) {
    try {
      const center = sel.__center as { latitude: number; longitude: number } | undefined;
      const roots = Object.keys(contextLayersByRoot);

      if (!center || roots.length === 0) {
        setLayerAttrs({});
        return;
      }

      const decodedByRoot: LayerAttrs = {};
      const proxy = (countyCfg as any).httpProxy;

      for (const rootRaw of roots) {
        const fsRoot = rootRaw.replace(/\/+$/, "");
        const layerIds = contextLayersByRoot[rootRaw];
        if (!fsRoot || !layerIds?.length) continue;

        const byLayer = await queryLayersAtPoint(
          fsRoot,
          layerIds,
          { latitude: center.latitude, longitude: center.longitude },
          proxy
        );

        if (!decodedByRoot[fsRoot]) decodedByRoot[fsRoot] = {};

        for (const idStr of Object.keys(byLayer)) {
          const layerId = Number(idStr);
          const rows = (byLayer as Record<number, any[]>)[layerId] || [];

          try {
            const layerUrl = `${fsRoot}/${layerId}`;
            const guide = await getDomainGuide(layerUrl);
            decodedByRoot[fsRoot][layerId] = rows.map((r) => {
              const a = { ...r };
              applyDomains(a, guide);
              return a;
            });
          } catch {
            decodedByRoot[fsRoot][layerId] = rows;
          }
        }
      }

      setLayerAttrs(decodedByRoot);
    } catch (e) {
      console.error("Context layer query failed", e);
      setLayerAttrs({});
    }
  }

  // When a row is clicked in the bottom grid:
  const handleRowSelect = (row: any) => {
    startBusy();
    setSelectedAttrs(row);
    mapRef.current?.highlightAndZoom?.(row.__geometry, row.__center);
    loadContextForSelection(row).finally(endBusy);
  };

  // When a search result is chosen in the sidebar:
  const handleSearchSelect = (attrsWithPos: any) => {
    startBusy();
    setSelectedAttrs(attrsWithPos);
    mapRef.current?.highlightAndZoom?.(attrsWithPos.__geometry, attrsWithPos.__center);
    loadContextForSelection(attrsWithPos).finally(endBusy);
  };

  const handleCondoUnitsFound = React.useCallback((units: any[]) => {
    // Show condo units in the results grid
    setResultRows(units);
  }, []);

  const handleMapIdentify = React.useCallback(
    async (payload: {
      center: { latitude: number; longitude: number };
      geometryJson?: any;
      attributes?: Record<string, any>;
      layerId?: number;
      layerTitle?: string;
    }) => {
      const title = (payload.layerTitle ?? "").toLowerCase();
      const isParcel = payload.layerId === 2 || payload.layerId === 0 || title.includes("parcel");
      const isBuilding = title.includes("building");

      if (!isParcel && !isBuilding) {
        return;
      }

      const sel = {
        ...(payload.attributes ?? {}),
        __center: payload.center,
        __geometry: payload.geometryJson ?? null,
      };

      startBusy();
      setSelectedAttrs(sel);

      // Apply domains to the clicked feature's attributes
      try {
        const parcelLayerUrl = (countyCfg as any).parcelLayer;
        if (parcelLayerUrl) {
          // parcelLayer already includes the layer ID, so use it directly
          const guide = await getDomainGuide(parcelLayerUrl);
          const decodedAttrs = { ...sel };
          applyDomains(decodedAttrs, guide);
          setSelectedAttrs(decodedAttrs);
        }
      } catch (e) {
        console.error("Failed to apply domains to clicked feature", e);
      }
      await loadContextForSelection(sel);
      endBusy();
    }, [countyCfg, contextLayersByRoot]);

  useEffect(() => {
    if (resultRows.length > 0) {
      if (!showResultsPanel) {
        setShowResultsPanel(true);
        setOpenResultsPanel(false);
        requestAnimationFrame(() => setOpenResultsPanel(true));
      }
    } else {
      setOpenResultsPanel(false);
      setShowResultsPanel(false);
    }
  }, [resultRows, showResultsPanel]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (appLoading) {
    return <GeoJobeLoader />;
  }

  return (
    <>
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={() => setShowDisclaimer(false)}
        onDecline={() => {
          window.location.href = config.disclaimerDeclineRedirect;
        }}
      />

      <div
        className="workspace-container"
        style={{ height: "100vh", display: "flex", flexDirection: "column" }}
      >
        <SplitPanel
          key="topPanel"
          {...topPanelProps}
          collapsible={false}
          initialSize={topPanelConfig.initialSize}
          maxResize={topPanelConfig.maxResize}
          label={topPanelConfig.label}
          resizable={false}
          fixedHeight={topPanelConfig.fixedHeight}
          style={topPanelConfig.style}
          theme={splitPanelTheme}
        >
          {isMobile ? (
            // Mobile layout: vertical split with sidebar+map on top, bottom bar below
            <SplitPanel
              key="mobileBottomPanel"
              slot="main-content"
              {...bottomPanelProps}
              split="h"
              collapseDirection="down"
              collapsible={showResultsPanel}
              collapsed={!openResultsPanel}
              initialSize={bottomPanelConfig.initialSize}
              maxResize={bottomPanelConfig.maxResize}
              label={bottomPanelConfig.label}
              resizable={false}
              style={bottomPanelConfig.style}
              theme={{ ...splitPanelTheme, dividerWidth: 0 }}
            >
              {/* Sidebar and Map in horizontal split */}
              <SplitPanel
                key="leftPanel"
                slot="main-content"
                {...leftPanelProps}
                initialSize={leftPanelConfig.initialSize}
                maxResize={leftPanelConfig.maxResize}
                label={leftPanelConfig.label}
                resizable={false}
                style={leftPanelConfig.style}
                theme={{ ...splitPanelTheme, dividerWidth: 0 }}
              >
                <div slot="collapse-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  <SideBar
                    config={countyCfg}
                    onUpdateResults={setResultRows}
                    onUpdateAttrs={handleSearchSelect}
                    selectedAttrs={selectedAttrs}
                    layerAttrs={layerAttrs}
                    onUpdateActiveRoot={setActiveRoot}
                  />
                </div>

                <ArcGISMap
                  key="map"
                  ref={mapRef}
                  slot="main-content"
                  itemId={(config as any).map?.itemId}
                  portalUrl={(config as any).map?.portalUrl}
                  onMapIdentify={handleMapIdentify}
                  onCondoUnitsFound={handleCondoUnitsFound}
                  onLoadingStart={startBusy}
                  onLoadingEnd={endBusy}
                />

                {/* Side panel button */}
                <button
                  slot="snap-btn"
                  data-btn-pos="4"
                  data-btn-open-offset="-22"
                  data-btn-closed-offset="12"
                  className="psa-icon-btn mobile-sidebar-toggle"
                  aria-label="Toggle sidebar"
                >
                  <span slot="snap-btn-close-icon">
                    <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true">
                      <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm5-5.246l-8-7.953 8-8.047h-5l-8 8.047 8 7.953z" />
                    </svg>
                  </span>
                  <span slot="snap-btn-open-icon" hidden>
                    <svg
                      className="svg-icon"
                      viewBox="0 0 32 32"
                      aria-hidden="true"
                      style={{ transform: "scaleX(-1)" }}
                    >
                      <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm5-5.246l-8-7.953 8-8.047h-5l-8 8.047 8 7.953z" />
                    </svg>
                  </span>
                </button>
              </SplitPanel>

              {/* Bottom bar spans full width */}
              <BottomBar
                slot="collapse-content"
                rows={resultRows}
                columns={(sharedConfig as any).searchResultsGridFields}
                onRowSelect={handleRowSelect}
                pageSizeOptions={(config as any).searchResultsGridPageSizeOptions}
                defaultPageSize={(config as any).searchResultsGridRowsPerPage}
                showColumnChooser={(config as any).resultsGridColumnsSelectable ?? true}
                activeServiceRoot={activeRoot}
              />

              <button
                slot="snap-btn"
                hidden={!showResultsPanel}
                data-btn-pos="50"
                data-btn-open-offset="0"
                data-btn-closed-offset="0"
                className="psa-icon-btn mobile-bottom-toggle"
                aria-label="Toggle results"
              >
                <span slot="snap-btn-close-icon">
                  <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true">
                    <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm-8-18.199l8 8 8-8v5l-8 8-8-8z" />
                  </svg>
                </span>
                <span slot="snap-btn-open-icon" hidden>
                  <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true">
                    <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm7.943-8.199l-7.953-8-8 8v-5l8-8 7.953 8v5z" />
                  </svg>
                </span>
              </button>
            </SplitPanel>

          ) : (
            // Desktop layout: original nested structure
            <SplitPanel
              key="leftPanel"
              slot="main-content"
              {...leftPanelProps}
              initialSize={leftPanelConfig.initialSize}
              maxResize={leftPanelConfig.maxResize}
              label={leftPanelConfig.label}
              resizable={false}
              style={leftPanelConfig.style}
              theme={{ ...splitPanelTheme, dividerWidth: 0 }}
            >
              <SideBar
                slot="collapse-content"
                config={countyCfg}
                onUpdateResults={setResultRows}
                onUpdateAttrs={handleSearchSelect}
                selectedAttrs={selectedAttrs}
                layerAttrs={layerAttrs}
                onUpdateActiveRoot={setActiveRoot}
              />

              {/* Map only (no bar) until results exist */}
              <SplitPanel
                key="bottomPanel"
                slot="main-content"
                {...bottomPanelProps}
                collapsible={showResultsPanel}
                collapsed={!openResultsPanel}
                initialSize={bottomPanelConfig.initialSize}
                maxResize={bottomPanelConfig.maxResize}
                label={bottomPanelConfig.label}
                resizable={false}
                style={bottomPanelConfig.style}
                theme={{ ...splitPanelTheme, dividerWidth: 0 }}
              >
                <ArcGISMap
                  key="map"
                  ref={mapRef}
                  slot="main-content"
                  itemId={(config as any).map?.itemId}
                  portalUrl={(config as any).map?.portalUrl}
                  onMapIdentify={handleMapIdentify}
                  onCondoUnitsFound={handleCondoUnitsFound}
                  onLoadingStart={startBusy}
                  onLoadingEnd={endBusy}
                />

                <BottomBar
                  slot="collapse-content"
                  rows={resultRows}
                  columns={(sharedConfig as any).searchResultsGridFields}
                  onRowSelect={handleRowSelect}
                  pageSizeOptions={(config as any).searchResultsGridPageSizeOptions}
                  defaultPageSize={(config as any).searchResultsGridRowsPerPage}
                  showColumnChooser={(config as any).resultsGridColumnsSelectable ?? true}
                  activeServiceRoot={activeRoot}
                />

                <button
                  slot="snap-btn"
                  hidden={!showResultsPanel}
                  data-btn-pos="98"
                  data-btn-open-offset="-43"
                  data-btn-closed-offset="-12"
                  className="psa-icon-btn"
                  aria-label="Toggle results"
                >
                  <span slot="snap-btn-close-icon">
                    <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true">
                      <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm-8-18.199l8 8 8-8v5l-8 8-8-8z" />
                    </svg>
                  </span>
                  <span slot="snap-btn-open-icon" hidden>
                    <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true" style={{ transform: "scaleX(-1)" }}>
                      <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm7.943-8.199l-7.953-8-8 8v-5l8-8 7.953 8v5z" />
                    </svg>
                  </span>
                </button>
              </SplitPanel>

              {/* Side panel button */}
              <button
                slot="snap-btn"
                data-btn-pos="3"
                data-btn-open-offset="-30"
                data-btn-closed-offset="12"
                className="psa-icon-btn"
                aria-label="Toggle sidebar"
              >
                <span slot="snap-btn-close-icon">
                  <svg className="svg-icon" viewBox="0 0 32 32" aria-hidden="true">
                    <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm5-5.246l-8-7.953 8-8.047h-5l-8 8.047 8 7.953z" />
                  </svg>
                </span>
                <span slot="snap-btn-open-icon" hidden>
                  <svg
                    className="svg-icon"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                    style={{ transform: "scaleX(-1)" }}
                  >
                    <path d="M16.047.447c-8.615 0-15.6 6.982-15.6 15.6 0 8.615 6.984 15.6 15.6 15.6 8.617 0 15.6-6.984 15.6-15.6 0-8.617-6.982-15.6-15.6-15.6zm0 28.799c-7.279 0-13.199-5.92-13.199-13.199s5.92-13.2 13.199-13.2 13.199 5.921 13.199 13.2-5.92 13.199-13.199 13.199zm5-5.246l-8-7.953 8-8.047h-5l-8 8.047 8 7.953z" />
                  </svg>
                </span>
              </button>
            </SplitPanel>
          )}

          <TopBar
            slot="collapse-content"
            paddingX={0}
            gap={16}
            {...(config as any).topBar}
          />
        </SplitPanel>
      </div>

      {busyCount > 0 && (
        <div className="app-loader-overlay" role="status" aria-live="polite">
          <CalciteLoader label="Loading" scale="m" />
        </div>
      )}
    </>
  );
};

export default Workspace;
